import React, { useState, useEffect } from 'react';
import { Select, Switch, Card, Tooltip } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { DynamicLinkPlatformAnalytic } from "../../../../dto/AnalyticDTO";
import { AnalyticsService } from "../../../../services/analytics.service";
import { CAMPAIGN_DEEP_LINK_URL } from "../../../../utils/config";
import { InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

interface DynamicLinkConversionAnalytic extends DynamicLinkPlatformAnalytic {
    installRate?: number;           // CLICK to APP_INSTALL
    firstOpenRate?: number;         // CLICK to APP_FIRST_OPEN
    reOpenRate?: number;            // CLICK to APP_RE_OPEN
    appEngagementRate?: number;     // (APP_FIRST_OPEN + APP_RE_OPEN) / CLICK
    redirectRate?: number;          // CLICK to REDIRECT (if REDIRECT exists)
}

const DynamicLinkGraph: React.FC = () => {
    const [durationDays, setDurationDays] = useState(7);
    const [rawAnalytics, setRawAnalytics] = useState<DynamicLinkPlatformAnalytic[]>([]);
    const [convertedAnalytics, setConvertedAnalytics] = useState<DynamicLinkConversionAnalytic[]>([]);
    const [loading, setLoading] = useState(false);
    const [showConversionRates, setShowConversionRates] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState<string>("ALL");

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await AnalyticsService.getDynamicLinkAnalytics(CAMPAIGN_DEEP_LINK_URL, durationDays);
            setRawAnalytics(response.data.platformAnalytics);

            // Calculate conversion rates for each platform
            const analytics = response.data.platformAnalytics.map(platform => {
                const clicks = platform.CLICK || 0;

                // Avoid division by zero
                const conversionData: DynamicLinkConversionAnalytic = {
                    ...platform,
                    installRate: clicks > 0 ? ((platform.APP_INSTALL || 0) / clicks) * 100 : 0,
                    firstOpenRate: clicks > 0 ? ((platform.APP_FIRST_OPEN || 0) / clicks) * 100 : 0,
                    reOpenRate: clicks > 0 ? ((platform.APP_RE_OPEN || 0) / clicks) * 100 : 0,
                    appEngagementRate: clicks > 0 ? (((platform.APP_FIRST_OPEN || 0) + (platform.APP_RE_OPEN || 0)) / clicks) * 100 : 0,
                    redirectRate: clicks > 0 && platform.REDIRECT ? (platform.REDIRECT / clicks) * 100 : undefined
                };

                return conversionData;
            });

            setConvertedAnalytics(analytics);
        } catch (error) {
            console.error('Error fetching dynamic link stats', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [durationDays]);

    // Get platforms for the dropdown
    const platforms = ['ALL', ...rawAnalytics.filter(item => item.platform !== 'ALL').map(item => item.platform)];

    // Filter data based on selected platform
    const filteredData = selectedPlatform === 'ALL'
        ? convertedAnalytics
        : convertedAnalytics.filter(item => item.platform === selectedPlatform);

    // Custom tooltip for showing both raw counts and conversion rates
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Card size="small" className="custom-tooltip" style={{ padding: '10px', border: '1px solid #d9d9d9' }}>
                    <p className="label" style={{ margin: 0, fontWeight: 'bold' }}>{`Platform: ${data.platform}`}</p>
                    <hr style={{ margin: '8px 0' }} />

                    {/* Always show raw counts */}
                    <p style={{ margin: '5px 0' }}><strong>Clicks:</strong> {data.CLICK || 0}</p>
                    {data.APP_INSTALL !== undefined && (
                        <p style={{ margin: '5px 0' }}><strong>Installs:</strong> {data.APP_INSTALL || 0}</p>
                    )}
                    {data.APP_FIRST_OPEN !== undefined && (
                        <p style={{ margin: '5px 0' }}><strong>First Opens:</strong> {data.APP_FIRST_OPEN || 0}</p>
                    )}
                    {data.APP_RE_OPEN !== undefined && (
                        <p style={{ margin: '5px 0' }}><strong>Re-Opens:</strong> {data.APP_RE_OPEN || 0}</p>
                    )}
                    {data.REDIRECT !== undefined && (
                        <p style={{ margin: '5px 0' }}><strong>Redirects:</strong> {data.REDIRECT || 0}</p>
                    )}

                    {/* Show conversion rates */}
                    <hr style={{ margin: '8px 0' }} />
                    <p style={{ margin: '5px 0' }}><strong>Install Rate:</strong> {data.installRate?.toFixed(2)}%</p>
                    <p style={{ margin: '5px 0' }}><strong>First Open Rate:</strong> {data.firstOpenRate?.toFixed(2)}%</p>
                    <p style={{ margin: '5px 0' }}><strong>Re-Open Rate:</strong> {data.reOpenRate?.toFixed(2)}%</p>
                    <p style={{ margin: '5px 0' }}><strong>App Engagement Rate:</strong> {data.appEngagementRate?.toFixed(2)}%</p>
                    {data.redirectRate !== undefined && (
                        <p style={{ margin: '5px 0' }}><strong>Redirect Rate:</strong> {data.redirectRate?.toFixed(2)}%</p>
                    )}
                </Card>
            );
        }
        return null;
    };

    const getChartTitle = () => {
        if (showConversionRates) {
            return "Dynamic Link Conversion Rates";
        }
        return "Dynamic Link Event Counts";
    };

    const renderMetricInfo = (title: string, description: string) => (
        <Tooltip title={description}>
      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '5px' }}>
        <InfoCircleOutlined style={{ fontSize: '14px' }} />
      </span>
        </Tooltip>
    );

    return (
        <div style={{ marginBottom: '2rem' }}>
            <div className="flex items-center justify-between pb-4">
                <h3 className="font-semibold text-xl">{getChartTitle()}</h3>
                <div className="flex items-center space-x-2">
                    <span>Show Conversion Rates</span>
                    <Switch
                        checked={showConversionRates}
                        onChange={(checked) => setShowConversionRates(checked)}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <Select
                    value={durationDays}
                    style={{ width: 120, marginRight: 10 }}
                    onChange={(value) => setDurationDays(value)}
                >
                    <Option value={7}>Last 7 Days</Option>
                    <Option value={14}>Last 14 Days</Option>
                    <Option value={30}>Last 30 Days</Option>
                </Select>

                <Select
                    value={selectedPlatform}
                    style={{ width: 120, marginLeft: 10 }}
                    onChange={(value) => setSelectedPlatform(value)}
                >
                    {platforms.map(platform => (
                        <Option key={platform} value={platform}>{platform}</Option>
                    ))}
                </Select>
            </div>

            {loading ? (
                <p>Loading dynamic link stats…</p>
            ) : (
                <>
                    {showConversionRates ? (
                        // Conversion rates view
                        <div>
                            <div className="mb-3">
                                <p className="text-sm text-gray-500">
                                    <strong>Install Rate</strong> {renderMetricInfo("Install Rate", "Percentage of clicks that resulted in app installation")} -
                                    <strong>First Open Rate</strong> {renderMetricInfo("First Open Rate", "Percentage of clicks that resulted in first app opens")} -
                                    <strong>Re-Open Rate</strong> {renderMetricInfo("Re-Open Rate", "Percentage of clicks that resulted in app re-opens")} -
                                    <strong>App Engagement</strong> {renderMetricInfo("App Engagement", "Percentage of clicks that resulted in any app open (first or re-open)")}
                                </p>
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="platform" />
                                    <YAxis domain={[0, 100]} label={{ value: 'Conversion %', angle: -90, position: 'insideLeft' }} />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="installRate" name="Install Rate" fill="#413ea0" />
                                    <Bar dataKey="firstOpenRate" name="First Open Rate" fill="#ff7300" />
                                    <Bar dataKey="reOpenRate" name="Re-Open Rate" fill="#82ca9d" />
                                    <Bar dataKey="appEngagementRate" name="App Engagement" fill="#8884d8" />
                                    {filteredData.some(item => item.redirectRate !== undefined) && (
                                        <Bar dataKey="redirectRate" name="Redirect Rate" fill="#ffc658" />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        // Raw event counts view
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={filteredData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="platform" />
                                <YAxis />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="CLICK" name="Clicks" fill="#8884d8" />
                                <Bar dataKey="APP_INSTALL" name="Installs" fill="#413ea0" />
                                <Bar dataKey="APP_FIRST_OPEN" name="First Opens" fill="#ff7300" />
                                <Bar dataKey="APP_RE_OPEN" name="Re-Opens" fill="#82ca9d" />
                                {filteredData.some(item => item.REDIRECT !== undefined) && (
                                    <Bar dataKey="REDIRECT" name="Redirects" fill="#ffc658" />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </>
            )}

            <div className="mt-4">
                <h4 className="font-semibold text-base mb-2">What These Metrics Mean:</h4>
                <ul className="list-disc pl-5 text-sm text-gray-600">
                    <li><strong>Install Rate:</strong> Percentage of link clicks that result in app installations</li>
                    <li><strong>First Open Rate:</strong> Percentage of clicks that lead to first-time app opens (new users)</li>
                    <li><strong>Re-Open Rate:</strong> Percentage of clicks that lead to existing users reopening the app</li>
                    <li><strong>App Engagement Rate:</strong> Combined percentage of clicks resulting in any app open (first + re-open)</li>
                    <li><strong>Redirect Rate:</strong> Percentage of clicks resulting in redirects (usually to app/play store)</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                    Higher conversion rates indicate more effective user acquisition and engagement through Almosafer's dynamic links.
                </p>
            </div>
        </div>
    );
};

export default DynamicLinkGraph;
